import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  bringForward,
  bringToFront,
  closeRightClickMenu,
  sendBackward,
  sendToBack,
  setLayerModalOpen,
} from "@/store/slideEditorSlice";
import { Button } from "@ai-teach/ui-web";
import { useEffect, useRef, useState } from "react";
import { BiLayerMinus, BiLayerPlus } from "react-icons/bi";
import { BsLayerBackward, BsLayerForward } from "react-icons/bs";

export function LayerControlModal() {
  const [position, setPosition] = useState<{ top: number; left: number } | undefined>(undefined);

  const selectedId = useAppSelector((state) => state.slideEditor.present.selectedId);
  const isLayerModalOpen = useAppSelector((state) => state.slideEditor.present.isLayerModalOpen);
  const rightClickMenuOpen = useAppSelector(
    (state) => state.slideEditor.present.rightClickMenu.isOpen
  );
  const dispatch = useAppDispatch();

  const modalRef = useRef<HTMLDivElement | null>(null);

  // Update position continuously to follow either the selected element or right-click menu
  useEffect(() => {
    if (!selectedId || !isLayerModalOpen) {
      setPosition(undefined);
      return;
    }

    let anchorElement = null;

    if (rightClickMenuOpen) {
      anchorElement = document.querySelector("[data-right-click-menu]");
    } else {
      anchorElement = document.querySelector(`[data-element-id="${selectedId}"]`);
    }

    if (!anchorElement) {
      setPosition(undefined);
      return;
    }

    const calculatePosition = (anchor: Element | null): { top: number; left: number } | null => {
      const modalRect = modalRef.current?.getBoundingClientRect();
      const modalWidth = modalRect?.width ?? 200; // Width of RightClickMenu
      const modalHeight = modalRect?.height ?? 280; // Approximate height
      const gap = 8;

      let top = 0;
      let left = 0;

      if (!anchor) return null;

      const anchorRect = anchor.getBoundingClientRect();

      if (rightClickMenuOpen) {
        left = anchorRect.left;

        const spaceBelow = window.innerHeight - anchorRect.bottom;
        const spaceAbove = anchorRect.top;

        if (spaceBelow >= modalHeight + gap) {
          // Position below the menu
          top = anchorRect.bottom + gap;
        } else if (spaceAbove >= modalHeight + gap) {
          // Position above the menu
          top = anchorRect.top - modalHeight - gap;
        } else {
          // Not enough space either way, position below and let it clip
          top = anchorRect.bottom + gap;
        }
      } else {
        left = anchorRect.right + gap;
        top = anchorRect.top;

        if (left + modalWidth > window.innerWidth) {
          // Check right boundary
          left = anchorRect.left - modalWidth - gap;
        }
      }

      if (left < 0) {
        // Check left boundary
        left = gap;
      }

      if (top + modalHeight > window.innerHeight) {
        // Check bottom boundary
        top = window.innerHeight - modalHeight - gap;
      }

      if (top < 0) {
        // Check top boundary
        top = gap;
      }

      return {
        top,
        left,
      };
    };

    const updatePosition = () => {
      const newPosition = calculatePosition(anchorElement);

      if (newPosition) {
        setPosition(newPosition);
      }
    };

    updatePosition();

    window.addEventListener("resize", updatePosition);

    let observer: MutationObserver | null = null;

    observer = new MutationObserver(updatePosition);
    observer.observe(anchorElement, { attributes: true, attributeFilter: ["style"] });

    return () => {
      window.removeEventListener("resize", updatePosition);
      if (observer) observer.disconnect();
    };
  }, [selectedId, isLayerModalOpen, rightClickMenuOpen]);

  const handleLayerControl = (type: string) => {
    if (!selectedId) return;

    switch (type) {
      case "back":
        dispatch(sendToBack(selectedId));
        break;
      case "front":
        dispatch(bringToFront(selectedId));
        break;
      case "backward":
        dispatch(sendBackward(selectedId));
        break;
      case "forward":
        dispatch(bringForward(selectedId));
        break;
    }

    // Close both modals after action
    dispatch(setLayerModalOpen(false));
    dispatch(closeRightClickMenu());
  };

  if (!isLayerModalOpen || !position) return null;

  return (
    <div
      data-layer-modal
      ref={modalRef}
      className="fixed bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border-2 border-slate-200/80 z-[900]"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      <div className="flex flex-col items-stretch gap-2 px-3 py-3 min-w-[200px]">
        {/* Header */}
        <div className="px-2 pb-2">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Layer Order
          </h3>
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

        {/* Bring Forward Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleLayerControl("forward")}
          className="justify-start gap-3"
        >
          <BiLayerPlus className="w-5 h-5" />
          <span className="text-sm font-medium">Bring forward</span>
        </Button>

        {/* Send Backward Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleLayerControl("backward")}
          className="justify-start gap-3"
        >
          <BiLayerMinus className="w-5 h-5" />
          <span className="text-sm font-medium">Send backward</span>
        </Button>

        <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

        {/* Bring to Front Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleLayerControl("front")}
          className="justify-start gap-3"
        >
          <BsLayerForward className="w-5 h-5" />
          <span className="text-sm font-medium">Bring to front</span>
        </Button>

        {/* Send to Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleLayerControl("back")}
          className="justify-start gap-3"
        >
          <BsLayerBackward className="w-5 h-5" />
          <span className="text-sm font-medium">Send to back</span>
        </Button>
      </div>
    </div>
  );
}
